import { NavLink, generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { PageBreadcrumbs, PageNavigation } from '@client/toolkit';
import { PermissionKeys, useGetCompaniesRoleCountsQuery } from '@client/api';
import { CompaniesPaths } from '../companies.router';
import { PageContentAnimation } from '../../../components/layout/page-content-animation';
import { PermissionBasedActionButton } from '../../../components/permission-based-button/permission-based-button';

export const CompaniesHeader = () => {
  const { t } = useTranslation('companies');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const navigate = useNavigate();

  const { data: roleCounts } = useGetCompaniesRoleCountsQuery();

  const handleClickCreateCompany = useCallback(
    () => navigate(generatePath(CompaniesPaths.CompanyCreate)),
    [navigate],
  );

  const tabs = useMemo(() => {
    return [
      {
        key: 'all',
        label: `${t('tabs.all')}${roleCounts ? ` (${roleCounts.all})` : ''}`,
        to: CompaniesPaths.Companies,
        end: true,
      },
      {
        key: 'owners',
        label: `${t('tabs.owners')}${roleCounts ? ` (${roleCounts.owners})` : ''}`,
        to: CompaniesPaths.Owners,
        end: false,
      },
      {
        key: 'managers',
        label: `${t('tabs.managers')}${roleCounts ? ` (${roleCounts.managers})` : ''}`,
        to: CompaniesPaths.Managers,
        end: false,
      },
      {
        key: 'tenants',
        label: `${t('tabs.tenants')}${roleCounts ? ` (${roleCounts.tenants})` : ''}`,
        to: CompaniesPaths.Tenants,
        end: false,
      },
    ];
  }, [t, roleCounts]);

  return (
    <>
      <PageContentAnimation className="flex flex-col gap-5">
        <PageNavigation>
          <PageBreadcrumbs>
            <PageBreadcrumbs.Link>{tGlobal('navigation.overview')}</PageBreadcrumbs.Link>
            <PageBreadcrumbs.Link>{tGlobal('entity.company', { context: 'plural' })}</PageBreadcrumbs.Link>
          </PageBreadcrumbs>
        </PageNavigation>
      </PageContentAnimation>
      <PageContentAnimation className="mb-1">
        <div className="flex items-end justify-between border-b border-border w-full">
          <div className="flex gap-8">
            {tabs.map((tab) => (
              <NavLink
                key={tab.key}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  classNames(
                    'pb-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'text-brand-primary border-b-2 border-brand-primary'
                      : 'text-white hover:text-brand-primary',
                  )
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
          <div className="pb-2">
            <PermissionBasedActionButton
              color="primary-dark"
              size="md"
              onClick={handleClickCreateCompany}
              permissions={[PermissionKeys.Tenant.CompanyCreate]}
              title={tCommon('createEntity', { entity: tGlobal('entity.company').toLowerCase() })}
            />
          </div>
        </div>
      </PageContentAnimation>
    </>
  );
};
